import { useState } from 'react'
import { useClipSources } from '@/hooks/useClipagem'

export default function SourceManager() {
  const { sources, loading, fetchSources, createSource, updateSource, deleteSource, toggleSourceStatus } = useClipSources()
  const [showForm, setShowForm] = useState(false)
  const [editSource, setEditSource] = useState<string | null>(null)
  const [form, setForm] = useState({
    name: '',
    url: '',
    scrape_url: '',
    frequency_minutes: 240,
    status: 'active' as 'active' | 'inactive',
    source_type: 'rss' as 'rss' | 'scraping',
  })
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  const handleSave = async () => {
    if (!form.name || !form.url) {
      showToast('Preencha nome e URL', 'error')
      return
    }
    setActionLoading(true)
    const payload = {
      name: form.name,
      url: form.url,
      scrape_url: form.scrape_url || null,
      frequency_minutes: form.frequency_minutes,
      status: form.status,
      source_type: form.source_type,
    }
    if (editSource) {
      const result = await updateSource(editSource, payload)
      if (result) {
        showToast('Fonte atualizada!')
        setShowForm(false)
        setEditSource(null)
        setForm({ name: '', url: '', scrape_url: '', frequency_minutes: 240, status: 'active', source_type: 'rss' })
      } else {
        showToast('Erro ao atualizar', 'error')
      }
    } else {
      const result = await createSource(payload)
      if (result) {
        showToast('Fonte criada!')
        setShowForm(false)
        setForm({ name: '', url: '', scrape_url: '', frequency_minutes: 240, status: 'active', source_type: 'rss' })
      } else {
        showToast('Erro ao criar', 'error')
      }
    }
    setActionLoading(false)
    fetchSources()
  }

  const handleDelete = async (id: string) => {
    setActionLoading(true)
    const success = await deleteSource(id)
    if (success) {
      showToast('Fonte excluída')
      setDeleteConfirm(null)
    } else {
      showToast('Erro ao excluir', 'error')
    }
    setActionLoading(false)
    fetchSources()
  }

  const handleToggle = async (id: string, currentStatus: string) => {
    const result = await toggleSourceStatus(id, currentStatus)
    if (result) {
      showToast(currentStatus === 'active' ? 'Fonte desativada' : 'Fonte ativada')
      fetchSources()
    } else {
      showToast('Erro ao alterar status', 'error')
    }
  }

  const handleEdit = (source: {
    id: string
    name: string
    url: string
    scrape_url?: string | null
    frequency_minutes: number
    status: 'active' | 'inactive'
    source_type?: 'rss' | 'scraping'
  }) => {
    setForm({
      name: source.name,
      url: source.url,
      scrape_url: source.scrape_url || '',
      frequency_minutes: source.frequency_minutes,
      status: source.status,
      source_type: source.source_type || 'rss',
    })
    setEditSource(source.id)
    setShowForm(true)
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-xl border border-alvinegro-border px-4 py-2">
            <span className="text-sm text-alvinegro-muted">
              {loading ? 'Carregando...' : `${sources.length} fonte(s) cadastrada(s)`}
            </span>
          </div>
        </div>
        <button
          onClick={() => {
            setForm({ name: '', url: '', scrape_url: '', frequency_minutes: 240, status: 'active', source_type: 'rss' })
            setEditSource(null)
            setShowForm(true)
          }}
          className="flex items-center gap-2 bg-alvinegro-black text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Nova Fonte
        </button>
      </div>

      {/* Source Health Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {sources.map((source) => (
          <div key={source.id} className="bg-white rounded-xl border border-alvinegro-border p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${source.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`} />
                <span className="text-sm font-medium text-alvinegro-black">{source.name}</span>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs border ${
                source.source_type === 'scraping'
                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                  : 'bg-orange-50 text-orange-700 border-orange-200'
              }`}>
                {source.source_type === 'scraping' ? 'Scraping' : 'RSS'}
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-alvinegro-muted truncate">{source.url}</p>
              {source.last_error && (
                <p className="text-xs text-red-600 bg-red-50 rounded px-2 py-1">
                  <i className="ri-error-warning-line mr-1"></i>
                  {source.last_error}
                </p>
              )}
              {source.last_fetched_at && (
                <p className="text-xs text-alvinegro-muted">
                  Última busca: {new Date(source.last_fetched_at).toLocaleString('pt-BR')}
                </p>
              )}
              {source.error_count && source.error_count > 0 && (
                <p className="text-xs text-yellow-600">
                  <i className="ri-alert-line mr-1"></i>
                  {source.error_count} erro(s) consecutivo(s)
                </p>
              )}
            </div>
            <div className="flex items-center gap-1 mt-3 pt-3 border-t border-alvinegro-border">
              <button
                onClick={() => handleToggle(source.id, source.status)}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light text-alvinegro-dark transition-colors cursor-pointer"
                title={source.status === 'active' ? 'Desativar' : 'Ativar'}
              >
                <i className={`ri-${source.status === 'active' ? 'pause' : 'play'}-line text-sm`}></i>
              </button>
              <button
                onClick={() => handleEdit(source)}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light text-alvinegro-dark transition-colors cursor-pointer"
                title="Editar"
              >
                <i className="ri-pencil-line text-sm"></i>
              </button>
              <button
                onClick={() => setDeleteConfirm(source.id)}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-50 text-red-600 transition-colors cursor-pointer"
                title="Excluir"
              >
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-alvinegro-border overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-10 h-10 rounded bg-alvinegro-light" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-alvinegro-light rounded w-1/4" />
                  <div className="h-3 bg-alvinegro-light rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-alvinegro-border bg-alvinegro-light">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Nome</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden md:table-cell">URL</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden sm:table-cell">Tipo</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden sm:table-cell">Frequência</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden sm:table-cell">Saúde</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody>
                {sources.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-alvinegro-muted">
                      <i className="ri-rss-line text-4xl mb-3 block"></i>
                      <p className="text-sm">Nenhuma fonte cadastrada</p>
                    </td>
                  </tr>
                ) : (
                  sources.map((source) => (
                    <tr key={source.id} className="border-b border-alvinegro-border hover:bg-alvinegro-light/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${source.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`} />
                          <span className="text-sm font-medium text-alvinegro-black">{source.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-xs text-alvinegro-muted truncate max-w-[200px] block">{source.url}</span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className={`px-2 py-0.5 rounded-full text-xs border ${
                          source.source_type === 'scraping'
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : 'bg-orange-50 text-orange-700 border-orange-200'
                        }`}>
                          {source.source_type === 'scraping' ? 'Scraping' : 'RSS'}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-xs text-alvinegro-muted">{source.frequency_minutes} min</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs border ${
                          source.status === 'active'
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-gray-50 text-gray-500 border-gray-200'
                        }`}>
                          {source.status === 'active' ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        {source.last_error ? (
                          <span className="text-xs text-red-600 flex items-center gap-1">
                            <i className="ri-error-warning-line"></i>
                            Erro
                          </span>
                        ) : source.error_count && source.error_count > 0 ? (
                          <span className="text-xs text-yellow-600 flex items-center gap-1">
                            <i className="ri-alert-line"></i>
                            {source.error_count} erro(s)
                          </span>
                        ) : (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <i className="ri-checkbox-circle-line"></i>
                            OK
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleToggle(source.id, source.status)}
                            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light text-alvinegro-dark transition-colors cursor-pointer"
                            title={source.status === 'active' ? 'Desativar' : 'Ativar'}
                          >
                            <i className={`ri-${source.status === 'active' ? 'pause' : 'play'}-line text-sm`}></i>
                          </button>
                          <button
                            onClick={() => handleEdit(source)}
                            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light text-alvinegro-dark transition-colors cursor-pointer"
                            title="Editar"
                          >
                            <i className="ri-pencil-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(source.id)}
                            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-50 text-red-600 transition-colors cursor-pointer"
                            title="Excluir"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-serif font-bold text-xl text-alvinegro-black">
                {editSource ? 'Editar Fonte' : 'Nova Fonte'}
              </h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-full bg-alvinegro-light flex items-center justify-center cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-alvinegro-dark mb-1">Nome da Fonte</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
                  placeholder="Ex: Globo Esporte"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-alvinegro-dark mb-1">Tipo de Fonte</label>
                <select
                  value={form.source_type}
                  onChange={(e) => setForm({ ...form, source_type: e.target.value as 'rss' | 'scraping' })}
                  className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black bg-white"
                >
                  <option value="rss">RSS Feed</option>
                  <option value="scraping">Web Scraping</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-alvinegro-dark mb-1">
                  {form.source_type === 'rss' ? 'URL do Feed RSS' : 'URL da Página para Scraping'}
                </label>
                <input
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
                  placeholder={form.source_type === 'rss' ? 'https://exemplo.com/feed.xml' : 'https://exemplo.com/noticias'}
                />
              </div>
              {form.source_type === 'rss' && (
                <div>
                  <label className="block text-sm font-medium text-alvinegro-dark mb-1">URL de Scraping de Fallback (opcional)</label>
                  <input
                    value={form.scrape_url}
                    onChange={(e) => setForm({ ...form, scrape_url: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
                    placeholder="https://exemplo.com/noticias (página HTML para scraping se RSS falhar)"
                  />
                  <p className="text-xs text-alvinegro-muted mt-1">Se o RSS falhar, tentará buscar nesta página HTML</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-alvinegro-dark mb-1">Frequência de Atualização (minutos)</label>
                <input
                  type="number"
                  value={form.frequency_minutes}
                  onChange={(e) => setForm({ ...form, frequency_minutes: parseInt(e.target.value) || 240 })}
                  className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
                  min={5}
                  max={1440}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-alvinegro-dark mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value as 'active' | 'inactive' })}
                  className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black bg-white"
                >
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </select>
              </div>
              <div className="flex items-center gap-3 pt-4">
                <button
                  onClick={handleSave}
                  disabled={actionLoading}
                  className="flex-1 bg-alvinegro-black text-white py-2.5 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {actionLoading ? 'Salvando...' : (editSource ? 'Salvar Alterações' : 'Criar Fonte')}
                </button>
                <button
                  onClick={() => setShowForm(false)}
                  className="flex-1 py-2.5 rounded-full border border-alvinegro-border text-sm font-medium text-alvinegro-dark hover:bg-alvinegro-light transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-delete-bin-line text-red-600 text-lg"></i>
              </div>
              <h3 className="font-semibold text-alvinegro-black">Excluir fonte</h3>
            </div>
            <p className="text-sm text-alvinegro-muted mb-6">Tem certeza que deseja excluir esta fonte permanentemente?</p>
            <div className="flex items-center gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-full border border-alvinegro-border text-sm font-medium text-alvinegro-dark hover:bg-alvinegro-light transition-colors cursor-pointer">Cancelar</button>
              <button onClick={() => handleDelete(deleteConfirm)} disabled={actionLoading} className="flex-1 py-2.5 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50">{actionLoading ? 'Excluindo...' : 'Excluir'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`rounded-xl px-5 py-3 shadow-lg border text-sm font-medium flex items-center gap-2 ${toast.type === 'success' ? 'bg-white border-green-200 text-green-700' : 'bg-white border-red-200 text-red-600'}`}>
            <i className={`${toast.type === 'success' ? 'ri-check-double-line' : 'ri-error-warning-line'}`}></i>
            {toast.message}
          </div>
        </div>
      )}
    </div>
  )
}